import {
  Avatar,
  Body,
  Heading,
  Icon,
  IconButton,
  Tooltip,
} from '@assembly-js/design-system';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Link } from 'src/components/Link';
import { Client, Company } from 'src/constants';
import { MenuAction } from 'src/features/crm/MenuAction';
import { SelectCompany } from 'src/features/forms/renderer/SelectCompany/SelectCompany';
import { useResizeObserver } from 'src/hooks/useResizeObserver';
import { isTemporaryCompany } from 'src/utils/CompanyUtils';

type NewCompany = {
  name: string;
};

const CompaniesListHeader = ({
  allowMultipleCompanies,
  onUpgradePlan,
  maxCompaniesAllowed,
  companies,
  setShouldExpandCompanySection,
  setIsAddingCompany,
}: {
  allowMultipleCompanies: boolean;
  onUpgradePlan: () => void;
  maxCompaniesAllowed: number;
  companies: Company[];
  setShouldExpandCompanySection: (value: boolean) => void;
  setIsAddingCompany: (value: boolean) => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-row items-center justify-between px-4">
      <Heading size="base">
        {t(($) => $.company, {
          count: companies.length,
        })}
      </Heading>

      <Tooltip
        disabled={allowMultipleCompanies}
        position="left"
        content={
          <TooltipTitle
            allowMultipleCompanies={allowMultipleCompanies}
            handleOnClick={onUpgradePlan}
            limit={maxCompaniesAllowed}
          />
        }
      >
        <span>
          <IconButton
            icon="Plus"
            size="sm"
            disabled={
              companies.length >= maxCompaniesAllowed || !allowMultipleCompanies
            }
            variant="minimal"
            onClick={(e) => {
              // Stops the accordion from toggling.
              e.stopPropagation();
              // expand the accordion when the user clicks the plus button
              setShouldExpandCompanySection(true);
              setIsAddingCompany(true);
            }}
            className="hover:bg-secondary-hover rounded"
          />
        </span>
      </Tooltip>
    </div>
  );
};

const CompaniesListWrapper = ({
  children,
  allowMultipleCompanies,
  onUpgradePlan,
  maxCompaniesAllowed,
  companies,
  setShouldExpandCompanySection,
  setIsAddingCompany,
}: {
  children: React.ReactNode;
  allowMultipleCompanies: boolean;
  onUpgradePlan: () => void;
  maxCompaniesAllowed: number;
  companies: Company[];
  shouldExpandCompanySection: boolean;
  setShouldExpandCompanySection: (value: boolean) => void;
  setIsAddingCompany: (value: boolean) => void;
}) => {
  return (
    <>
      <CompaniesListHeader
        allowMultipleCompanies={allowMultipleCompanies}
        onUpgradePlan={onUpgradePlan}
        maxCompaniesAllowed={maxCompaniesAllowed}
        companies={companies}
        setShouldExpandCompanySection={setShouldExpandCompanySection}
        setIsAddingCompany={setIsAddingCompany}
      />
      {children}
    </>
  );
};

export type ClientCompanyManagerProps = {
  maxCompaniesAllowed: number;
  onUpgradePlan: () => void;
  client: Client;
  companies: Company[];
  onRemoveCompany: ({
    company,
    client,
  }: {
    company: Company;
    client: Client;
  }) => void;
  onAddCompany: ({
    company,
    client,
  }: {
    company: Company | NewCompany;
    client: Client;
  }) => void;
};

export function ClientCompanyManager({
  client,
  companies,
  onRemoveCompany,
  onAddCompany,
  maxCompaniesAllowed,
  onUpgradePlan,
}: ClientCompanyManagerProps) {
  const { t } = useTranslation();
  const [isAddingCompany, setIsAddingCompany] = React.useState(false);
  const [shouldExpandCompanySection, setShouldExpandCompanySection] =
    React.useState(true);
  const allowMultipleCompanies = companies.length < maxCompaniesAllowed;

  return (
    <CompaniesListWrapper
      allowMultipleCompanies={allowMultipleCompanies}
      onUpgradePlan={onUpgradePlan}
      maxCompaniesAllowed={maxCompaniesAllowed}
      companies={companies}
      shouldExpandCompanySection={shouldExpandCompanySection}
      setShouldExpandCompanySection={setShouldExpandCompanySection}
      setIsAddingCompany={setIsAddingCompany}
    >
      <div className="flex flex-1 flex-col pb-6 pt-3">
        {isAddingCompany && (
          <div key="add-company-form" className="px-4">
            <SelectCompany
              placeholder={t(
                ($) => $.messages.autocomplete_find_or_create_company,
              )}
              autoFocus
              onChange={(item) => {
                const company = item?.company;
                onAddCompany({
                  company: company ?? {
                    name: item?.label ?? '',
                  },
                  client,
                });
                setIsAddingCompany(false);
              }}
              onBlur={() => {
                setIsAddingCompany(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setIsAddingCompany(false);
                }
              }}
              excludeCompanyIds={client.fields.companyIds}
            />
          </div>
        )}

        {companies.length === 0 && !isAddingCompany && (
          <div className="flex flex-1 flex-col text-center">
            <Body className="text-gray-dark">
              {t(($) => $.messages.click_plus_to_add_company)}
            </Body>
          </div>
        )}

        {companies.map((company) => (
          <CompanyListItem
            client={client}
            company={company}
            onRemoveCompany={onRemoveCompany}
            key={`${client.id}#${company.id}`}
          />
        ))}
      </div>
    </CompaniesListWrapper>
  );
}

const CompanyListItem = ({
  client,
  company,
  onRemoveCompany,
}: {
  client: Client;
  company: Company;
  onRemoveCompany: ({
    company,
    client,
  }: {
    company: Company;
    client: Client;
  }) => void;
}) => {
  const { t } = useTranslation();
  const { ref: sidebarRef, rect } = useResizeObserver<HTMLDivElement>();
  // Improved truncation detection using callback ref
  const [isTruncated, setIsTruncated] = React.useState(false);
  const handleNameRef = React.useCallback(
    (node: HTMLSpanElement | null) => {
      if (node && rect.width) {
        setIsTruncated(node.scrollWidth > node.clientWidth);
      }
    },
    [rect.width],
  );
  const handleCompanyLinkMouseDown = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (e.metaKey || e.ctrlKey) {
      window.open(`/companies/${company.id}`, '_blank');
      e.preventDefault();
    }
  };

  const actions = useMemo(
    () => [
      {
        icon: <Icon icon={'Close'} />,
        name: t(($) => $.actions.remove_company),
        isDelete: true,
        onClick: () => {
          onRemoveCompany({
            company,
            client,
          });
        },
      },
    ],
    [t, company, onRemoveCompany, client],
  );

  return (
    <div
      key={company.id}
      ref={sidebarRef}
      className="flex flex-row flex-1 items-center gap-2 py-1 hover:bg-secondary-hover px-4 cursor-pointer"
    >
      <Link
        href={`/companies/${company.id}`}
        onMouseDown={handleCompanyLinkMouseDown}
        className={clsx('flex flex-1 items-center gap-2', {
          'pointer-events-none opacity-50': isTemporaryCompany(company),
        })}
      >
        <Avatar
          src={company.fields.avatarImageURL ?? ''}
          fallbackColor={company.fields.fallbackColor}
          text={
            company.fields.name
              ? company.fields.name.charAt(0).toUpperCase()
              : ''
          }
          size="sm"
          variant="rounded"
          alt={company.fields.name}
        />
        <Tooltip
          content={<Body size="sm">{company.fields.name}</Body>}
          position="top"
          disabled={!isTruncated}
        >
          <div
            className="flex flex-col text-black-heading hover:text-black-heading"
            //  The maximum width is dynamically calculated based on the ResizeSidebar
            style={
              rect.width
                ? {
                    maxWidth: Math.max(60, rect.width - 70),
                  }
                : undefined
            }
          >
            <span className="text-sm font-normal truncate" ref={handleNameRef}>
              {company.fields.name}
            </span>
          </div>
        </Tooltip>
      </Link>
      <MenuAction actions={actions} />
    </div>
  );
};

const TooltipTitle = ({
  allowMultipleCompanies,
  handleOnClick,
  limit,
}: {
  allowMultipleCompanies: boolean;
  handleOnClick: () => void;
  limit: number;
}) => {
  const { t } = useTranslation();

  return (
    <Body size="sm">
      {allowMultipleCompanies ? (
        t(($) => $.messages.only_x_companies_per_client, {
          limit: limit,
        })
      ) : (
        <>
          <span>{t(($) => $.messages.multiple_companies_pro_advanced)}</span>
          <span
            className="underline  text-[0.75rem] text-[#0C41BB] hover:underline cursor-pointer"
            onClick={handleOnClick}
          >
            {t(($) => $.messages.upgrade)}
          </span>
        </>
      )}
    </Body>
  );
};
